/* eslint-disable @typescript-eslint/no-unused-vars */
import usePopUpsV2 from "components/PopUpManagerV2/hooks/usePopUpsV2";
import { useGoogleAnalyticsContext, useWebStoreContext } from "contexts";
import { useAppDispatch } from "hooks";
import {
	AcceptedFriendReferralTracker,
	FriendReferralSuccessfullyAccepted,
	IFriendReferral
} from "interfaces";
import { FeatureType } from "interfaces/feature-tracker";
import { useEffect, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
	useAcceptedReferralMutation,
	useGetFeatureTrackerQuery,
	usePostFrTrackerMetadataMutation
} from "redux/apis";
import { setIsCheckingFr } from "redux/slices";

enum FeatureParams {
	accept = "acceptFriendReferral",
	autoLogin = "loginType"
}

const LAST_SEEN_SUCCESSFUL_REFERRAL_KEY = "lastSeenSuccessfulReferral";

const useFriendInvite = () => {
	const { open } = usePopUpsV2();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { pathname } = useLocation();
	const [searchParams] = useSearchParams();
	const [acceptedReferral] = useAcceptedReferralMutation();
	const { reportPlaygamiEvent } = useGoogleAnalyticsContext();
	const [postFrTrackerMetadata] = usePostFrTrackerMetadataMutation();
	const { authToken, openLoginToContinueDialog } = useWebStoreContext();
	const isAutoLogin = searchParams.has(FeatureParams.autoLogin);
	const isAcceptingFriendReferral = searchParams.has(FeatureParams.accept);
	const queuedSuccessfulReferralCount = useRef(0);

	const { data, isLoading }: { data?: IFriendReferral; isLoading: boolean } =
		useGetFeatureTrackerQuery(
			{
				FeatureType: FeatureType.FRIEND_REFERRAL,
				isAuthenticated: !!authToken?.value
			},
			{
				skip: document.hidden,
				pollingInterval: 1000 * 60,
				refetchOnFocus: true
			}
		);

	const openSuccessPopup = async (
		successfullyAccepted: FriendReferralSuccessfullyAccepted[],
		nextSeenSuccessfulReferral: number
	) => {
		await postFrTrackerMetadata({
			key: LAST_SEEN_SUCCESSFUL_REFERRAL_KEY,
			value: nextSeenSuccessfulReferral
		}).unwrap();

		successfullyAccepted.forEach((referral) => {
			open({
				variant: "friend-invite-success-claim-popup",
				payload: {
					rewardId: referral.inviterRewardId,
					friendName: referral.friendName || "Your friend",
					type: referral.friendUserId
				}
			});
		});
	};

	const removeQueryParam = (param: string) => {
		searchParams.delete(param);
		navigate(
			{
				pathname,
				search: searchParams.toString()
			},
			{ replace: true }
		);
	};

	const onAcceptReferral = async (referralId: string) => {
		await acceptedReferral({ referralId })
			.unwrap()
			.then((res?: { referral?: AcceptedFriendReferralTracker }) => {
				reportPlaygamiEvent("tp.web_frontend", {
					event_name: "launch_webstore_using_web_friend_referral_deeplink",
					event_type: "invitation_deeplink",
					action: "launched_success",
					source: "web_friend_referral",
					type: res?.referral?.userId,
					login_type: "auto"
				});
				open({
					variant: "friend-invite-accepted-popup",
					id: "friend-invite-accepted-popup",
					replaceIfExists: true,
					payload: {
						data: res?.referral,
						extraRewards: false
					}
				});
			})
			.catch((err) => {
				console.error("Error accepting friend referral:", err);
				open({
					variant: "friend-invite-accepted-popup",
					id: "friend-invite-accepted-popup",
					replaceIfExists: true,
					payload: {
						alreadyAccepted: true
					}
				});
			})
			.finally(() => {
				removeQueryParam(FeatureParams.accept);
			});
		dispatch(setIsCheckingFr(false));
	};

	useEffect(() => {
		if (!isLoading && data?.tracker) {
			const count =
				Number(data?.tracker?.metadata?.lastSeenSuccessfulReferral) || 0;
			const successfulCount =
				data?.tracker?.referrals?.successfullyAcceptedCount || 0;
			const queuedCount = Math.max(
				count,
				queuedSuccessfulReferralCount.current
			);

			if (queuedCount < successfulCount) {
				const successfullyAccepted =
					data?.tracker?.referrals?.successfullyAccepted?.slice(
						queuedCount,
						successfulCount
					) || [];
				const nextSeenSuccessfulReferral =
					queuedCount + successfullyAccepted.length;

				if (!successfullyAccepted.length) {
					return;
				}

				queuedSuccessfulReferralCount.current = nextSeenSuccessfulReferral;
				openSuccessPopup(
					successfullyAccepted,
					nextSeenSuccessfulReferral
				).catch((err) => {
					queuedSuccessfulReferralCount.current = count;
					console.error("Error updating friend referral metadata:", err);
				});
			}
		}
	}, [
		isLoading,
		data?.tracker?.metadata?.lastSeenSuccessfulReferral,
		data?.tracker?.referrals?.successfullyAcceptedCount,
		data?.tracker?.referrals?.successfullyAccepted?.length
	]);

	useEffect(() => {
		if (isAcceptingFriendReferral) {
			if (isAutoLogin) {
				return;
			}
			if (!authToken?.value) {
				openLoginToContinueDialog();
				dispatch(setIsCheckingFr(false));
				return;
			}
			const referralId = searchParams.get(FeatureParams.accept);
			if (referralId) {
				onAcceptReferral(referralId);
			}
		} else {
			dispatch(setIsCheckingFr(false));
		}
	}, [isAcceptingFriendReferral, isAutoLogin]);
};

export default useFriendInvite;
